import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import type { ChatMessage } from '../../auth-config/types';
import ChatMessageBubble, { TypingIndicator } from './ChatMessageBubble';

interface Props {
  messages: ChatMessage[];
  isSending: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export interface ChatMessageListHandle {
  scrollToBottom: () => void;
}

const ChatMessageList = forwardRef<ChatMessageListHandle, Props>(
  ({ messages, isSending, isLoadingMore, hasMore, onLoadMore }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    // Used to preserve scroll position after prepending older messages
    const prevScrollHeight = useRef<number>(0);

    // Expose scrollToBottom to parent
    useImperativeHandle(ref, () => ({
      scrollToBottom() {
        const el = containerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      },
    }));

    // Auto-scroll to bottom when messages change (new message sent/received)
    // Only if user is already near the bottom (within 120px)
    const scrollToBottomIfNear = useCallback(() => {
      const el = containerRef.current;
      if (!el) return;
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distFromBottom < 120) {
        el.scrollTop = el.scrollHeight;
      }
    }, []);

    useEffect(() => {
      scrollToBottomIfNear();
    }, [messages.length, isSending]);

    // After prepending older messages restore scroll position
    useEffect(() => {
      const el = containerRef.current;
      if (!el || prevScrollHeight.current === 0) return;
      const added = el.scrollHeight - prevScrollHeight.current;
      el.scrollTop += added;
      prevScrollHeight.current = 0;
    }, [messages.length]);

    // IntersectionObserver on sentinel at top of list
    useEffect(() => {
      const sentinel = sentinelRef.current;
      if (!sentinel) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && hasMore && !isLoadingMore) {
            // Save scroll height before adding old messages
            if (containerRef.current) {
              prevScrollHeight.current = containerRef.current.scrollHeight;
            }
            onLoadMore();
          }
        },
        { root: containerRef.current, threshold: 0.1 }
      );

      observer.observe(sentinel);
      return () => observer.disconnect();
    }, [hasMore, isLoadingMore, onLoadMore]);

    return (
      <div
        ref={containerRef}
        id="chat-message-list"
        className="flex-1 overflow-y-auto px-3 py-3"
        style={{ scrollBehavior: 'smooth' }}
      >
        {/* Scroll-up sentinel */}
        <div ref={sentinelRef} className="h-1" />

        {/* Load-more indicator */}
        {isLoadingMore && (
          <div className="flex justify-center py-2">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                />
              </svg>
              Loading older messages…
            </div>
          </div>
        )}

        {/* No messages yet */}
        {messages.length === 0 && !isSending && (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-8">
            <div className="w-14 h-14 rounded-full bg-[#5624d0]/10 flex items-center justify-center">
              <svg
                className="w-7 h-7 text-[#5624d0]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Ask your MaxtiManch AI</p>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                Have a question about this topic?<br />Type below to get an instant answer.
              </p>
            </div>
          </div>
        )}

        {/* Message bubbles */}
        {messages.map((msg) => (
          <ChatMessageBubble key={msg.messageId} message={msg} />
        ))}

        {/* Typing indicator */}
        {isSending && <TypingIndicator />}
      </div>
    );
  }
);

ChatMessageList.displayName = 'ChatMessageList';

export default ChatMessageList;
